import { useEffect, useRef, useState } from "react";
import api, { apiErr, downloadBlob, blobErrDetail } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { InlineError } from "@/components/InlineError";
import { FileSpreadsheet, Loader2, Trash2, Plus, ArrowLeft, AlertTriangle, CheckCircle2, Download } from "lucide-react";

const blank = () => ({ name: "", unit: "unité", price: "", cost: "", quantity: "", category_id: "", confidence: "high", issues: [], duplicate_id: null, existing_price: null, duplicate_in_batch: false, _sel: true, _dup: "update_price" });

export default function ProductImport({ open, onClose, onDone }) {
  const { current } = useActivity();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [cats, setCats] = useState([]);
  const [lastFile, setLastFile] = useState(null);
  const [unmapped, setUnmapped] = useState([]);
  const [colSel, setColSel] = useState({});
  const [recap, setRecap] = useState(false);
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRows([]); setErr(""); setRecap(false);
    setUnmapped([]); setLastFile(null); setColSel({});
    api.get(`/categories?activity_id=${current.id}`).then((r) => setCats(r.data || [])).catch(() => {});
  }, [open]);

  const normalizeRows = (list) => (list || []).map((r) => ({ ...blank(), ...r, _sel: !(r.issues && r.issues.includes("Nom manquant")), _dup: r.duplicate_id ? "update" : "create_new" }));

  const parseFile = async (f) => {
    if (f) setLastFile(f);
    const file = f || lastFile;
    if (!file) return;
    setLoading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("activity_id", current.id);
      const { data } = await api.post("/products/import/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUnmapped(data.unmapped_headers || []); setColSel({});
      const rr = normalizeRows(data.rows);
      setRows(rr);
      if (rr.length === 0) setErr("Aucune ligne exploitable trouvée dans le fichier. Vérifiez les colonnes (Produit, Quantité, Unité, Prix).");
    } catch (e) { setErr(apiErr(e.response?.data?.detail, e)); }
    finally { setLoading(false); }
  };
  const downloadModel = () => {
    const lines = [
      "Nom;Catégorie;Code-barres (facultatif);Marque (facultatif);Variante de (facultatif);Libellé de la variante (facultatif);Unité de base;Seuil de réapprovisionnement;Coût d'achat par unité;Prix de vente par unité;Vente uniquement par conditionnement (Oui/Non);Remise sur ce produit (%) (facultatif);Quantité initiale (facultatif)",
      "Eau Salama 1,5 L — Bouteille;Eaux minérales;;Salama;;Bouteille;Bouteille;24;0,30;0,50;Non;0;120",
      "Eau Salama 1,5 L — Pack de 6;Eaux minérales;;Salama;;Pack de 6;Pack;5;1,60;2,70;Non;0;20",
      "Eau Salama 50 cl — Pack de 24;Eaux minérales;;Salama;;Pack de 24;Pack;3;5,50;8,50;Non;0;10",
      "Riz parfumé;Épicerie;;;;;sac;5;20000;25000;Non;0;40",
      "Gravillon (vendu par camion);Matériaux;;;;;m³;0;0;0;Oui;0;0",
    ];
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, "modele-produits-biashara.csv");
  };

  const downloadXlsx = async () => {
    setErr("");
    try {
      const res = await api.get(`/products/import/template.xlsx?activity_id=${current.id}`, { responseType: "blob" });
      downloadBlob(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "modele-produits-biashara.xlsx");
    } catch (e) { setErr(apiErr(await blobErrDetail(e), e)); }
  };

  const downloadFilled = async () => {
    setErr("");
    try {
      const res = await api.get(`/products/import/template.xlsx?activity_id=${current.id}&filled=true`, { responseType: "blob" });
      downloadBlob(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "produits-a-mettre-a-jour.xlsx");
    } catch (e) { setErr(apiErr(await blobErrDetail(e), e)); }
  };

  const applyMapping = async () => {
    const maps = {};
    Object.entries(colSel).forEach(([h, fld]) => { if (fld) maps[h] = fld; });
    if (Object.keys(maps).length) { try { await api.post("/products/import/column-map", { maps }); } catch (e) {} }
    parseFile();
  };

  const setCell = (i, k, v) => setRows((rr) => rr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const delRow = (i) => setRows((rr) => rr.filter((_, idx) => idx !== i));
  const addRow = () => setRows((rr) => [...rr, blank()]);

  const selected = rows.filter((r) => r._sel && (r.name || "").trim());
  const toCreate = selected.filter((r) => !r.duplicate_id || r._dup === "create_new").length;
  const toUpdate = selected.filter((r) => r.duplicate_id && (r._dup === "update" || r._dup === "update_price")).length;
  const stockCount = selected.filter((r) => Number(r.quantity) > 0 && r._dup !== "skip").length;

  const commit = async () => {
    setCommitting(true); setErr("");
    try {
      const payload = {
        activity_id: current.id,
        rows: selected.filter((r) => r._dup !== "skip").map((r) => ({
          name: (r.name || "").trim(), unit: (r.unit || "unité").trim() || "unité",
          price: Number(r.price || 0), cost: Number(r.cost || 0), quantity: Number(r.quantity || 0),
          category_id: r.category_id || null,
          category: r.category || null, barcode: r.barcode || null, brand: r.brand || null,
          variant_of: r.variant_of || null, variant_label: r.variant_label || null,
          reorder_level: Number(r.reorder_level || 0), discount_percent: Number(r.discount_percent || 0),
          divisible: r.divisible ?? null, packagings: r.packagings || [],
          with_packaging: r.with_packaging ?? null,
          action: r.duplicate_id ? (r._dup === "update_price" ? "update_price" : r._dup === "update" ? "update" : "create") : "create",
          existing_id: r.duplicate_id || null,
        })),
      };
      const { data } = await api.post("/products/import/commit", payload);
      if (data.duplicate_batch) {
        toast.warning(data.errors?.[0] || "Ce fichier a déjà été importé — aucun doublon créé.");
      } else {
        toast.success(`${data.created} créé(s), ${data.updated} mis à jour, ${data.stock_entries} entrée(s) de stock`);
        if ((data.errors || []).length) toast.error(`${data.errors.length} ligne(s) en erreur : ${data.errors.slice(0, 3).join(" · ")}`);
      }
      setRecap(false); onDone && onDone(); onClose();
    } catch (e) { setRecap(false); setErr(apiErr(e.response?.data?.detail, e)); }
    finally { setCommitting(false); }
  };

  const rowBad = (r) => (r.issues && r.issues.length > 0);
  const hasRows = rows.length > 0;

  return (
    <Modal open={open} onClose={onClose} title="Importer des produits" wide testId="product-import-modal"
      footer={hasRows ? (
        <>
          <button className="btn-outline" onClick={() => { setRows([]); setUnmapped([]); setLastFile(null); setErr(""); }} data-testid="import-back-btn"><ArrowLeft className="w-4 h-4" /> Recommencer</button>
          <button className="btn-primary" onClick={() => setRecap(true)} disabled={selected.length === 0} data-testid="import-review-btn">Vérifier et importer ({selected.length})</button>
        </>
      ) : <button className="btn-outline" onClick={onClose}>Fermer</button>}>

      {!hasRows && (
        <div className="space-y-3 text-center py-6" data-testid="import-file">
          <FileSpreadsheet className="w-10 h-10 mx-auto text-[#15803D]" />
          <p className="text-sm text-[#64748B]">Fichier <b>.xlsx</b> ou <b>.csv</b> reprenant les mêmes champs que la fiche produit : Nom, Catégorie, Code-barres (facultatif), Marque (facultatif), Variante de (facultatif), Libellé de la variante (facultatif), Unité de base, Seuil de réapprovisionnement, Coût d'achat, Prix de vente, Remise (%) (facultatif), <b>Quantité initiale (facultatif)</b>.</p>
          <p className="text-xs text-[#94A3B8]">Chaque conditionnement (bouteille, pack, carton, sac…) = une ligne distincte, avec son propre coût, prix et stock. La <b>Quantité initiale</b> crée automatiquement le stock de départ dans cette activité — aucune conversion entre conditionnements. Toutes les colonnes sont reconnues automatiquement.</p>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="import-file-input" onChange={(e) => parseFile(e.target.files?.[0])} />
          <div className="flex flex-wrap gap-2 justify-center">
            <button className="btn-primary" onClick={() => fileRef.current?.click()} disabled={loading} data-testid="import-file-pick">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Choisir un fichier</button>
            <button className="btn-outline" onClick={downloadXlsx} data-testid="import-file-xlsx"><Download className="w-4 h-4" /> Modèle Excel (.xlsx)</button>
            <button className="btn-outline" onClick={downloadFilled} data-testid="import-file-filled"><Download className="w-4 h-4" /> Mes produits (à mettre à jour)</button>
            <button className="btn-outline" onClick={downloadModel} data-testid="import-file-model"><Download className="w-4 h-4" /> Modèle CSV</button>
          </div>
          <InlineError message={err} title="Import impossible" />
        </div>
      )}

      {hasRows && (
        <div className="space-y-3">
          {unmapped.length > 0 && (
            <div className="p-3 rounded-xl bg-[#FFFBEB] border border-[#FDE68A] space-y-2" data-testid="import-colmap">
              <p className="text-sm text-[#92400E] font-medium">Colonnes non reconnues — associez-les (mémorisé pour vos prochains imports) :</p>
              {unmapped.map((h) => (
                <div key={h} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs bg-white border border-[#FDE68A] rounded px-2 py-1 min-w-[120px] truncate">{h}</span>
                  <span>→</span>
                  <select className="inp !py-1.5 max-w-[200px]" value={colSel[h] || ""} onChange={(e) => setColSel((s) => ({ ...s, [h]: e.target.value }))}>
                    <option value="">Ignorer</option>
                    <option value="name">Produit</option>
                    <option value="quantity">Quantité</option>
                    <option value="unit">Unité</option>
                    <option value="price">Prix</option>
                    <option value="cost">Coût</option>
                  </select>
                </div>
              ))}
              <button className="btn-outline !py-1.5" onClick={applyMapping} data-testid="import-apply-colmap">Appliquer et mémoriser</button>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3">
            <div className="overflow-x-auto border border-[#E7ECF3] rounded-xl">
              <table className="w-full min-w-[1040px] text-sm" data-testid="import-table">
                <thead><tr>
                  <th className="th !py-2 w-8"></th>
                  <th className="th !py-2 min-w-[220px]">Produit</th>
                  <th className="th !py-2 w-24">Qté</th>
                  <th className="th !py-2 w-28">Unité</th>
                  <th className="th !py-2 w-44">Catégorie</th>
                  <th className="th !py-2 w-32">Prix</th>
                  <th className="th !py-2 w-32">Coût</th>
                  <th className="th !py-2 w-52">État</th>
                  <th className="th !py-2 w-10"></th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`row ${rowBad(r) ? "bg-[#FEF2F2]" : r.confidence === "low" ? "bg-[#FFFBEB]" : ""}`} data-testid={`import-row-${i}`}>
                      <td className="td !py-1.5"><input type="checkbox" className="w-4 h-4 accent-[#2563EB]" checked={r._sel} onChange={(e) => setCell(i, "_sel", e.target.checked)} data-testid={`import-select-${i}`} /></td>
                      <td className="td !py-1.5"><input className="inp !py-1.5" value={r.name} onChange={(e) => setCell(i, "name", e.target.value)} data-testid={`import-name-${i}`} /></td>
                      <td className="td !py-1.5"><input className="inp !py-1.5" value={r.quantity} onChange={(e) => setCell(i, "quantity", e.target.value)} /></td>
                      <td className="td !py-1.5"><input className="inp !py-1.5" value={r.unit} onChange={(e) => setCell(i, "unit", e.target.value)} /></td>
                      <td className="td !py-1.5"><select className="inp !py-1.5" value={r.category_id || ""} onChange={(e) => setCell(i, "category_id", e.target.value)} data-testid={`import-cat-${i}`}><option value="">—</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></td>
                      <td className="td !py-1.5"><input className="inp !py-1.5" value={r.price} onChange={(e) => setCell(i, "price", e.target.value)} data-testid={`import-price-${i}`} /></td>
                      <td className="td !py-1.5"><input className="inp !py-1.5" value={r.cost} onChange={(e) => setCell(i, "cost", e.target.value)} /></td>
                      <td className="td !py-1.5 text-xs">
                        {rowBad(r) && <span className="badge-danger text-[10px]"><AlertTriangle className="w-3 h-3" /> {r.issues.join(", ")}</span>}
                        {!rowBad(r) && r.cost_auto && <span className="badge-info text-[10px]" title="Coût unitaire calculé automatiquement à partir du coût du lot ÷ facteur">Coût auto</span>}
                        {!rowBad(r) && r.confidence === "low" && <span className="badge-warning text-[10px]">À vérifier{r.note ? ` · ${r.note}` : ""}</span>}
                        {r.duplicate_id && (
                          <select className="inp !py-1 mt-1 text-xs" value={r._dup} onChange={(e) => setCell(i, "_dup", e.target.value)} data-testid={`import-dup-${i}`}>
                            <option value="update">Existe — tout mettre à jour</option>
                            <option value="update_price">MAJ prix seulement ({r.existing_price})</option>
                            <option value="create_new">Créer quand même</option>
                            <option value="skip">Ignorer</option>
                          </select>
                        )}
                        {!rowBad(r) && r.confidence !== "low" && !r.duplicate_id && <span className="badge-success text-[10px]"><CheckCircle2 className="w-3 h-3" /> OK</span>}
                      </td>
                      <td className="td !py-1.5"><button onClick={() => delRow(i)} className="text-[#B91C1C]" data-testid={`import-del-${i}`}><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <button onClick={addRow} className="btn-ghost !py-1 text-xs text-[#2563EB]" data-testid="import-add-row"><Plus className="w-3.5 h-3.5" /> Ajouter une ligne</button>

          <div className="p-3 rounded-xl bg-[#F0FDF4] border border-[#BBF7D0] text-sm text-[#166534]" data-testid="import-stock-note">
            Les lignes avec une <b>Quantité initiale &gt; 0</b> créent automatiquement le stock de départ de cette activité (mouvement « réception », motif « Stock initial — Import produits »). Les lignes sans quantité créent le produit avec un stock à zéro.
          </div>
          <InlineError message={err} title="Import impossible" />
        </div>
      )}

      <ConfirmDialog open={recap} danger={false} title="Récapitulatif de l'import" loading={committing}
        message={`À valider : ${toCreate} produit(s) à créer, ${toUpdate} prix à mettre à jour, ${stockCount} entrée(s) de stock initial. Rien n'est enregistré tant que vous ne confirmez pas.`}
        confirmLabel="Confirmer l'import" onConfirm={commit} onClose={() => setRecap(false)} />
    </Modal>
  );
}
